import { useState, useEffect } from "react";
import { fetchUserHealth } from "./api";
import type { UserHealth } from "./api";
import { ConversationThread } from "./ConversationThread";

interface UserHealthCardProps {
  userId: string;
  email: string;
  tier: string;
  created: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffH = Math.floor((now - then) / 3600000);
  if (diffH < 1) return "just now";
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 1) return "today";
  if (diffD === 1) return "Yesterday";
  if (diffD < 7) return `${diffD}d ago`;
  return formatDate(iso);
}

const HEALTH_LABELS: Record<string, string> = {
  growing: "Growing",
  steady: "Steady",
  declining: "Declining",
  churning: "Churning",
};

const HEALTH_COLORS: Record<string, string> = {
  growing: "var(--color-success)",
  steady: "var(--color-text-placeholder)",
  declining: "var(--color-property-warm)",
  churning: "var(--color-error-text)",
};

export function UserHealthCard({ userId, email, tier, created }: UserHealthCardProps) {
  const [data, setData] = useState<UserHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedConvId, setExpandedConvId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchUserHealth(userId)
      .then(setData)
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="cx-admin-health">
        <p className="cx-text-secondary" style={{ fontSize: "var(--cx-text-xs)" }}>
          Loading health data...
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="cx-admin-health">
        <p className="cx-text-secondary" style={{ fontSize: "var(--cx-text-xs)" }}>
          {error ? "Failed to load user data" : "No data"}
        </p>
      </div>
    );
  }

  const m = data.metrics;
  const trendUp = m.trend_pct >= 0;
  const trendLabel = trendUp
    ? `\u25B2 ${m.trend_pct}% vs last week`
    : `\u25BC ${Math.abs(m.trend_pct)}% vs last week`;

  return (
    <div className="cx-admin-health">
      {/* Header */}
      <div className="cx-admin-health-header">
        <div>
          <div className="cx-admin-health-name">{email}</div>
          <div className="cx-admin-health-meta">
            {tier} &middot; Joined {formatDate(created)} &middot; {data.total_conversations} conversations
          </div>
        </div>
        <div className="cx-admin-health-status">
          <span className={`cx-admin-health-dot cx-admin-health-dot--${data.health}`} />
          <span style={{ color: HEALTH_COLORS[data.health] }}>
            {HEALTH_LABELS[data.health] ?? data.health}
          </span>
        </div>
      </div>

      {/* Metrics */}
      <div className="cx-admin-metrics">
        <div className="cx-admin-metric">
          <div className="cx-admin-metric-label">This week</div>
          <div className="cx-admin-metric-value">{m.messages_this_week}</div>
          <div className={`cx-admin-metric-sub ${trendUp ? "cx-admin-metric-sub--up" : "cx-admin-metric-sub--down"}`}>
            {trendLabel}
          </div>
        </div>
        <div className="cx-admin-metric">
          <div className="cx-admin-metric-label">Tool depth</div>
          <div className="cx-admin-metric-value">
            {m.tools_used} / {m.tools_total}
          </div>
          <div className="cx-admin-metric-sub cx-admin-metric-sub--accent">{m.tools_label}</div>
        </div>
        <div className="cx-admin-metric">
          <div className="cx-admin-metric-label">Avg conversation</div>
          <div className="cx-admin-metric-value">{m.avg_conversation_length} msgs</div>
          <div className="cx-admin-metric-sub">{m.conv_label}</div>
        </div>
        <div className="cx-admin-metric">
          <div className="cx-admin-metric-label">Friction</div>
          <div className="cx-admin-metric-value">{m.friction_count}</div>
          <div className="cx-admin-metric-sub">
            {m.friction_count === 0 ? "No issues this week" : `${m.friction_count} issue${m.friction_count !== 1 ? "s" : ""} this week`}
          </div>
        </div>
      </div>

      {/* Top tools */}
      {data.top_tools.length > 0 && (
        <div className="cx-admin-tools-section">
          <div className="cx-admin-tools-label">Most used tools</div>
          <div className="cx-admin-tool-pills">
            {data.top_tools.map((tool) => (
              <span key={tool.name} className="cx-admin-tool-pill">
                {tool.name} ({tool.count})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent conversations */}
      {data.recent_conversations.length > 0 && (
        <div>
          <div className="cx-admin-tools-label">Recent conversations</div>
          {data.recent_conversations.map((conv) => {
            const isExpanded = expandedConvId === conv.conversation_id;
            return (
              <div key={conv.conversation_id}>
                <div
                  className={`cx-admin-conv-row ${isExpanded ? "cx-admin-conv-row--expanded" : ""}`}
                  onClick={() => setExpandedConvId(isExpanded ? null : conv.conversation_id)}
                >
                  <span className="cx-admin-conv-title">{conv.title || "Untitled"}</span>
                  <span className="cx-admin-conv-meta">
                    {conv.message_count} msgs &middot; {relativeTime(conv.updated_at)}{" "}
                    &middot; {isExpanded ? "\u25BC" : "\u25B6"}
                  </span>
                </div>
                {isExpanded && <ConversationThread conversationId={conv.conversation_id} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
